import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import PropTypes from "prop-types";
import {
  Avatar,
  Box,
  Grid,
  Divider,
  Button,
  Popover,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TextField,
  InputAdornment,
  Paper,
  Pagination,
  PaginationItem,
  Select,
  MenuItem,
  Typography,
  usePagination,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@mui/styles";
import planningImg from "../../assets/images/home/planning.svg";
import saveImg from "../../assets/images/home/save.svg";
import fileBrowser from "../../icons/file-browser.svg";
import { ReactComponent as FilterIcon } from "../../assets/icons/filter.svg";
import ConfirmDialog from "../../components/inbox/ConfirmDialog";
import * as Util from "../../services/global.service";

import Search from "@mui/icons-material/Search";
import cssVariables from "../../theme/cssVariables";

// rest service
import { getInboxLimit, patchReadInbox } from "../../actions/inbox";

function StylePaper(props) {
  return (
    <Paper sx={{ padding: "0.5rem 1.5rem", minHeight: "65vh" }}>
      {props.children}
    </Paper>
  );
}

function NoData() {
  const { t } = useTranslation();
  return (
    <TableRow>
      <TableCell colSpan={4}>
        <Box
          textAlign="center"
          sx={{ fontWeight: 300, flexFlow: "column" }}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Box>
            <img src={fileBrowser} alt="browser" />
          </Box>
          <Box>
            <Typography style={{color: '#A0A0A0'}}>{t("label.nodata")}</Typography>
          </Box>
        </Box>
      </TableCell>
    </TableRow>
  );
}

function CustomPagination(props) {
  const useStyles = makeStyles({
    paginationRoot: {
      color: `${cssVariables.color.primary}`,
      backgroundColor: "white!important",
      minWidth: "30px!important",
      padding: "5px!important",
    },
    paginationSelected: {
      color: "white!important",
      border: "1px solid rgba(93, 161, 150, 0.5)!important",
      backgroundColor: `${cssVariables.color.primary}!important`,
      minWidth: "30px!important",
      padding: "5px!important",
    },
  });
  const classes = useStyles();
  const List = styled("ul")({
    listStyle: "none",
    padding: 0,
    margin: 0,
    display: "flex",
  });
  const { items } = usePagination({
    count: props.count,
    showFirstButton: true,
    showLastButton: true,
  });
  return (
    <nav>
      <List>
        {items.length > 0 ? (
          items.map(({ page, type, selected, ...item }, index) => {
            let children = null; 
            selected = type === "page" && page === props.selected ? props.selected : selected
            if (type === "start-ellipsis" || type === "end-ellipsis") {
              children = "…";
            } else if (type === "page") {
              children = (
                <Button
                  {...item}
                  size="small"
                  type="button"
                  style={{
                    fontWeight: selected ? "bold" : undefined,
                  }}
                  variant="outlined"
                  classes={{
                    root: selected
                      ? classes.paginationSelected
                      : classes.paginationRoot,
                  }}
                  sx={{ margin: "0 5px" }}
                >
                  {page}
                </Button>
              );
            } else if (type === "first" || type === "last") { 
              children = (
                <Button
                  {...item}
                  disabled={ type === "first" ? props.selected === 1 ? true : false : props.selected === props.count ? true : false}
                  size="small"
                  type="button"
                  style={{
                    fontWeight: selected ? "bold" : undefined,
                  }}
                  variant="outlined"
                  classes={{
                    root: selected
                      ? classes.paginationSelected
                      : classes.paginationRoot,
                  }}
                  sx={{ margin: "0 5px" }}
                >
                  {type === "first" ? <>&lt;&lt;</> : <>&gt;&gt;</>}
                </Button>
              );
            } else {
              children = (
                <Button
                  {...item}
                  disabled={ type === "previous" ? props.selected === 1 ? true : false : props.selected === props.count ? true : false}
                  size="small"
                  type="button"
                  variant="outlined"
                  classes={{
                    root: classes.paginationRoot,
                  }}
                >
                  {type === "previous"
                    ? "ก่อนหน้า"
                    : type === "next"
                    ? "ถัดไป"
                    : ""}
                </Button>
              );
            }
            return <li key={index}>{children}</li>;
          })
          
        ) : (
          <></>
        )}
        {props.setCurrentPage(items)}
      </List>
    </nav>
  );
}

function formatData(data) {
  let result = {
    received: [],
    sent: []
  }

  if (data.out ?? false) {
    for (const key of Object.keys(data.out)) {
      let tmpList = []
      tmpList = data.out[key].map(item => {
        return {
          serial: item.message_id ?? "",
          name: item.requester.fullname ?? "",
          requestTitle: item.requester.position ?? "",
          requestType: item.rb_pa_step.name ?? "",
          requestSubject: item.rb_pa ?? "",
          requestName: item.name ?? "",
          createdDate: item.created_at ?? "",
          type: item.rb_pa_step.step ?? "",
          profileImage: item.requester.photo ?? "",
          status: item.status ?? "",
          processStatus: key,
          isRead: item.is_read,
          statusName: item.status_name
        }
      })
      result.sent = [...result.sent, ...tmpList]
    }
  }

  if (data.in ?? false) {
    for (const key of Object.keys(data.in)) {
      let tmpList = []
      tmpList = data.in[key].map(item => {
        return {
          serial: item.message_id ?? "",
          name: item.requester.fullname ?? "",
          requestTitle: item.requester.position ?? "",
          requestType: item.rb_pa_step.name ?? "",
          requestSubject: item.rb_pa ?? "",
          requestName: item.name ?? "",
          createdDate: item.created_at ?? "",
          type: item.rb_pa_step.step ?? "",
          profileImage: item.requester.photo ?? "",
          status: item.status ?? "",
          processStatus: key,
          isRead: item.is_read,
          statusName: item.status_name
        }
      })
      result.received = [...result.received, ...tmpList]
    }
  }
  return result
}
const InboxLayout = (props) => {
  const _ = require("lodash");
  const [list, setList] = React.useState({ received:[], sent:[] });
  const [filteredList, setFilteredList] = React.useState([]);
  const [groupedReq, setGroupReq] = React.useState([]);
  const [perPage, setPerPage] = React.useState(5);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [totalPage, setTotalPage] = React.useState(1);
  const [mockupRequest, setMockupRequest] = React.useState([
    {
      processStatus: 'wait',
      id: 1, 
      isRead: false, 
      name: 'Request for approval', 
      createdDate: '2022-01-17', 
      type: 1, 
      requestType: 'Goal Template for Operation Level 2022', 
      requestSubject: 'Jan 22 - Dec 22',
      profileImage: false,
      requestName: 'Sorraya Boonya',
      requestTitle: 'HRM Officer',
      statusName: 'In Progress',
    },
    {
      processStatus: 'wait',
      id: 2, 
      isRead: false, 
      name: 'Request for approval', 
      createdDate: '2022-01-17', 
      type: 2, 
      requestType: 'Goal Template for Operation Level 2022', 
      requestSubject: 'Jan 22 - Dec 22',
      profileImage: false,
      requestName: 'Lullalin Sukjai',
      requestTitle: 'Senior HRM Officer',
      statusName: 'In Progress',
    },
  ])
  const dispatch = useDispatch();
  const inboxList = useSelector((state) => state.Inbox.inboxList)
  const navigate = useNavigate();

  // first page load & set state
  useEffect(() => {
    // dispatch(getInboxLimit())
  }, [])
  useEffect(() => {
    const tmpState = {...inboxList}
    setList(formatData(tmpState))
  }, [inboxList])
  useEffect(() => {
    const tmpState = {...list}
    setFilteredList(tmpState[props.selectedInbox])
  }, [list])


  // go to detail page
  const handleGoToDetail = (serial) => {
    dispatch(patchReadInbox(serial))
    const inboxType = props.selectedInbox;
    if (inboxType) navigate("/app/inbox/" + inboxType + "/" + serial, { replace: false });
  };

  const handlePerPageChange = (event) => {
    setPerPage(event.target.value);
  };
  const handleSetCurrentPage = (value) => {
    const getSelectedPage = value.filter(item => item.type === "page" && item.selected)
    setCurrentPage(getSelectedPage.length > 0 ? getSelectedPage[0].page : 1)
  };
  const handleTotalPage = () => {
    // console.log(filteredList)
    const List = filteredList.length > 0 ? filteredList.filter((item) => item.processStatus === tab) : []
    const totalPage =
      List.length > 0 ? Math.ceil(List.length / perPage) === 0
        ? 1
        : Math.ceil(List.length / perPage) : 1
    setTotalPage(totalPage);
  };

  const [tab, setTab] = React.useState("wait");
  const handleChange = (event, newValue) => {
    setCurrentPage(1);
    setTab(newValue);
  };

  useEffect(() => {
    handleTotalPage();
  }, [tab]);


  // handle search
  const [search, setSearchValue] = React.useState("");
  const handleSearch = (event) => {
    setSearchValue(event.target.value);
  };

  // update listwhen inbox type changed
  useEffect(() => {
    if(props.selectedInbox === "received") {
      setTab("wait")
    }else if (props.selectedInbox === "sent") {
      setTab("inprogress")
    }
    switch (props.selectedInbox) {
      case "received":
        setFilteredList(list.received);
        break;
      case "sent":
        setFilteredList(list.sent);
        break;
      default:
        break;
    }
  }, [props.selectedInbox]);
  // update qty for each status when list changed
  useEffect(() => {
    setGroupReq(_.groupBy(list[props.selectedInbox], (loRow) => loRow.processStatus));
    handleTotalPage();
  }, [filteredList]);
  // update list when search changed
  useEffect(() => {
    let result = [];
    if (search !== "") {
      result = list[props.selectedInbox].filter((item) => {
        return Object.keys(item).some((key) => {
          if (item[key]) {
            return item[key].toString().toLowerCase().indexOf(search) !== -1;
          }
          return item[key];
        });
      });
    } else {
      result = list[props.selectedInbox];
    }
    setFilteredList(result);
  }, [search]);
  
  // data for show
  const currentTableData = useMemo(() => {
    handleTotalPage();
    // console.log(filteredList)
    const firstPageIndex = (currentPage - 1) * perPage;
    const lastPageIndex = firstPageIndex + perPage;
    return filteredList
      ?.filter((item) => item.processStatus === tab)
      .sort((a, b) => {
        return (a.isRead === b.isRead)? 0 : a.isRead? 1 : -1;
      })
      .slice(firstPageIndex, lastPageIndex)
  }, [filteredList, currentPage, props.selectedInbox, tab, perPage]);

  // styles
  const useStyles = makeStyles({
    root: {
      fontSize: "16px",
      lineHeight: "24px",
      fontWeight: "400",
      // color: "#A0A0A0"
    },
    iconContainer: {
      width: 40,
      height: 40,
      borderRadius: 5,
      marginTop: 3,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
    profileContainer: {
      width: 40,
      height: 40,
      borderRadius: "50%",
      marginTop: 3,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
    statusBadge: {
      width: "fit-content",
      minWidth: "120px",
      borderRadius: "5px",
      padding: "10px",
    },
    draftBg: {
      background: "#EFB76D26"
    },
    draftText: {
      color: "#EFB76D"
    },
    pendingBg: {
      background: "#F280261A"
    },
    pendingText: {
      color: "#F28026"
    },
    approveBg: {
      background: "#5DA19626"
    },
    approveText: {
      color: "#5DA196"
    },
    rejectedBg: {
      background: "#CC605826"
    },
    rejectedText: {
      color: "#CC6058"
    },
    cancelBg: {
      background: "#CC605826"
    },
    cancelText: {
      color: "#CC6058",
    },
    cancelPendingBg: {
      background: "#82828226"
    },
    cancelPendingText: {
      color: "#828282",
    },
    completedBg: {
      background: "rgba(93, 161, 150, 0.15);",
    },
    completedText: {
      color: "#5DA196",
    },
    blueBg: {
      background: "#6694CC",
    },
    purpleBg: {
      background: "#7580BD",
    },
    btnContainer: {
      width: "100%",
      display: "flex",
      alignItems: "flex-start",
      flexGrow: 1,
    },
    btnActive: {
      background: cssVariables.color.primary,
      color: `${cssVariables.color.white}!important`,
      "&:hover": {
        backgroundColor: "#4f8a80",
      },
    },
    btnInactive: {
      "&:hover": {
        backgroundColor: "#5da19626",
      },
    },
    btn: {
      transition: "0.2s",
      cursor: "pointer",
      borderRadius: "5px",
      padding: "10px",
      color: cssVariables.color.black,
    },
    paginationRoot: {
      color: `${cssVariables.color.primary}`,
      backgroundColor: "white!important",
      minWidth: "30px!important",
      padding: "5px!important",
    },
    paginationSelected: {
      color: "white!important",
      border: "1px solid rgba(93, 161, 150, 0.5)!important",
      backgroundColor: `${cssVariables.color.primary}!important`,
      minWidth: "30px!important",
      padding: "5px!important",
    },
    tableHover: {
      transition: "0.5s",
      "&:hover": {
        background: "#4043490f",
        cursor: "pointer",
      },
    },
  });

  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <Grid container spacing={2}>
      <Grid item xs>
        <Grid container>
          <Grid item xs="auto">
            <Tabs
              onChange={handleChange}
              value={tab}
              indicatorColor="primary"
              textColor="primary"
              aria-label="Tabs"
              TabIndicatorProps={{
                style: {
                  height: "8px",
                },
              }}
            >
              <Tab
                value="wait"
                label={
                  <span className={classes.root} style={{textTransform: 'none'}}>
                    {t("label.status.pending")} 
                    {/* ({groupedReq?.wait?.length ?? 0}) */}
                  </span>
                }
                sx={{display: props.selectedInbox === "received" ? "block" : "none"}}
              />
              <Tab
                value="inprogress"
                label={
                  <span className={classes.root} style={{textTransform: 'none'}}>
                    {t("label.status.processing")}
                    {/* ({groupedReq?.inprogress?.length ?? 0}) */}
                  </span>
                }
              /> 
              <Tab
                value="done"
                label={
                  <span className={classes.root} style={{textTransform: 'none'}}>
                    {t("label.status.completed")}
                    {/* ({groupedReq?.done?.length ?? 0}) */}
                  </span>
                }
              />
            </Tabs>
          </Grid>
          {/* <Grid item xs display="flex" flexDirection="row-reverse">
            <Box ml={1}>
              <Button
                variant="outlined"
                color="black"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={handleOpenFilterPopover}
              >
                <Box
                  mr={1}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <FilterIcon />
                </Box>
                <Box display="flex" alignItems="center" justifyContent="center">
                  {t("delete")}
                  ตัวกรอง
                </Box>
              </Button>
            </Box>
          </Grid> */}
          <Grid item sm={12} xs={12} md={4} ml="auto">
            <TextField
              id="input-with-icon-textfield"
              fullWidth
              placeholder={t('label.search')}
              size="small"
              sx={{ background: "#fff" }}
              className="searchTextField-h48"
              value={search}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search style={{ color: "#C4C4C4" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
        <Grid container mt={2}>
          <Grid item xs sm md lg>
            <TableContainer component={StylePaper}>
              <Table sx={{ width: "100%" }} aria-label="simple table">
                <TableHead sx={{ borderBottom: "1px solid #e0e0e0" }}>
                  <TableRow>
                    <TableCell align="left">
                      <Typography
                        fontWeight={500}
                        fontSize={16}
                        lineHeight="24px"
                        sx={{ color: "#A0A0A0" }}
                      >
                        {t("label.name")}
                      </Typography>
                    </TableCell>
                    <TableCell align="left">
                      <Typography
                        fontWeight={500}
                        fontSize={16}
                        lineHeight="24px"
                        sx={{ color: "#A0A0A0" }}
                      >
                        {t("label.request.item")}
                      </Typography>
                    </TableCell>
                    <TableCell align="left">
                      <Typography
                        fontWeight={500}
                        fontSize={16}
                        lineHeight="24px"
                        sx={{ color: "#A0A0A0" }}
                      >
                        {t("label.request.requester")}
                      </Typography>
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{ width: "20%", maxWidth: "150px" }}
                    >
                      <Typography
                        fontWeight={500}
                        fontSize={16}
                        lineHeight="24px"
                        sx={{ color: "#A0A0A0" }}
                      >
                        {t("label.request.status")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mockupRequest.length > 0 ? (
                    mockupRequest.map((row) =>
                      row.processStatus !== tab ? (
                        <></>
                      ) : (
                        <TableRow
                          key={row.id}
                          sx={{
                            // "&:last-child td, &:last-child th": { border: 0 },
                            background: row.isRead ? "#FAFAFA" : "#fff",
                          }}
                          classes={{ root: classes.tableHover }}
                          onClick={() => handleGoToDetail(row.serial)}
                        >
                          <TableCell align="left">
                            <Box
                              display="flex"
                              sx={{
                                flexFlow: "column",
                                textAlign: "left",
                                marginLeft: 2,
                                position: "relative",
                              }}
                            >
                              {!row.isRead ? (
                                <div
                                  style={{
                                    position: "absolute",
                                    marginLeft: "-20px",
                                    marginTop: "10px",
                                    width: "10px",
                                    height: "10px",
                                    background: "#CC6058",
                                    borderRadius: "50%",
                                  }}
                                ></div>
                              ) : (
                                <></>
                              )}
                              <Typography
                                variant="h4"
                                fontWeight={!row.isRead ? 500 : 300}
                                fontSize={18}
                                lineHeight="27px"
                              >
                                {row.name}
                              </Typography>
                              <Typography variant="sub_label" lineHeight="27px">
                                {Util.dateDayTimeFormat(row.createdDate)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="left">
                            <Box display="flex">
                              <div
                                className={`${classes.iconContainer} ${
                                  row.type == 1
                                    ? classes.blueBg
                                    : classes.purpleBg
                                }`}
                              >
                                <img
                                  src={row.type == 1 ? planningImg : saveImg}
                                  alt="bannerImage"
                                />
                              </div>
                              <Box
                                display="flex"
                                ml={2}
                                sx={{ flexFlow: "column" }}
                              >
                                <Typography
                                  variant="h4"
                                  fontWeight={500}
                                  fontSize={18}
                                  lineHeight="27px"
                                >
                                  {row.requestType}
                                </Typography>
                                <Typography
                                  variant="h6"
                                  fontSize={12}
                                  lineHeight="17px"
                                  sx={{ color: "#6B6B6B" }}
                                  className="fw-400"
                                >
                                  {row.requestSubject}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell align="left">
                            <Box display="flex">
                              { 
                                row.profileImage ?
                                <img
                                  className={`${classes.profileContainer}`}
                                  src={row.profileImage}
                                  alt="profile"
                                /> : <Avatar className={`${classes.profileContainer}`}/>
                              }
                              <Box
                                display="flex"
                                alignItems="flex-start"
                                ml={2}
                                sx={{ flexFlow: "column" }}
                              >
                                <Typography
                                  variant="h4"
                                  fontWeight={500}
                                  fontSize={18}
                                  lineHeight="27px"
                                >
                                  {row.requestName}
                                </Typography>
                                <Typography
                                  variant="h5"
                                  fontSize={14}
                                  lineHeight="21px"
                                  sx={{ color: "#404349" }}
                                  className="fw-400"
                                >
                                  {row.requestTitle}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <div
                              className={`${classes.statusBadge} ${
                                row.statusName === "ระหว่างการแก้ไข" || row.statusName === "ระหว่างดำเนินการ" || row.statusName === 'In Progress'
                                    ? classes.draftBg
                                    : row.statusName === "ระหว่างการอนุมัติ"
                                    ? classes.pendingBg
                                    : row.statusName === "อนุมัติเสร็จสิ้น"
                                    ? classes.approveBg
                                    : row.statusName === "ปฏิเสธ"
                                    ? classes.rejectedBg
                                    : row.statusName === "ยกเลิกเสร็จสิ้น"
                                    ? classes.cancelBg
                                    : classes.cancelPendingBg
                              }`}
                            >
                              <Typography
                                className={
                                  row.statusName === "ระหว่างการแก้ไข" || row.statusName === "ระหว่างดำเนินการ" || row.statusName === 'In Progress'
                                    ? classes.draftText
                                    : row.statusName === "ระหว่างการอนุมัติ"
                                    ? classes.pendingText
                                    : row.statusName === "อนุมัติเสร็จสิ้น"
                                    ? classes.approveText
                                    : row.statusName === "ปฏิเสธ"
                                    ? classes.rejectedText
                                    : row.statusName === "ยกเลิกเสร็จสิ้น"
                                    ? classes.cancelText
                                    : classes.cancelPendingText
                                }
                                fontWeight={500}
                              >
                                {/* {t("label.status." + row.status)} */}
                                {row.statusName}
                              </Typography>
                            </div>
                          </TableCell>
                        </TableRow>
                      )
                    )
                  ) : (
                    <NoData></NoData>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
export default InboxLayout;
